/**
 * LiveIndicator — small "🔴 LIVE" pill that pulses while the SSE stream is
 * connected. Shows reconnecting state with an amber dot when the stream
 * blinks, and a muted grey when it's closed. Tooltip exposes the running
 * event count for power users.
 *
 * Used on the Stage view header to reassure players that the score pipe is
 * healthy even during quiet news periods.
 */
import { Activity, WifiOff } from 'lucide-react';

const STATUS_META = {
  live:        { label: 'LIVE',        dot: '#22c55e', anim: 'live-pulse-green' },
  reconnecting:{ label: 'RECONN',      dot: '#f59e0b', anim: 'live-pulse-amber' },
  connecting:  { label: 'CONNECTING',  dot: '#a78bfa', anim: 'live-pulse-violet' },
  closed:      { label: 'OFFLINE',     dot: '#6b7280', anim: null },
};

export default function LiveIndicator({ status = 'connecting', eventsTotal = 0, lastEventAt = null }) {
  const meta = STATUS_META[status] || STATUS_META.connecting;
  const showOffline = status === 'closed';
  const lastSec = lastEventAt ? Math.floor((Date.now() - lastEventAt) / 1000) : null;
  const title = `Score stream ${status}${eventsTotal ? ` · ${eventsTotal} events received` : ''}${lastSec != null ? ` · last ${lastSec}s ago` : ''}`;

  return (
    <span
      className="live-indicator"
      data-status={status}
      data-testid="stage-live-indicator"
      title={title}
      aria-label={title}
    >
      {showOffline ? (
        <WifiOff size={11} strokeWidth={2.4} style={{ color: meta.dot }} aria-hidden="true" />
      ) : (
        <span
          className="live-indicator-dot"
          style={{ background: meta.dot, animation: meta.anim ? `${meta.anim} 1500ms ease-in-out infinite` : 'none' }}
          aria-hidden="true"
        />
      )}
      <span className="live-indicator-label">{meta.label}</span>
      {status === 'live' && eventsTotal > 0 && (
        <span className="live-indicator-count" aria-hidden="true">
          <Activity size={9} strokeWidth={2.6} />
          {eventsTotal}
        </span>
      )}
    </span>
  );
}
